import type { DiagramDocument, DiagramNode, DeviceType, NetworkLifecycle } from './types.js';
import { snapPosition } from './diagramGrid.js';
import {
  defaultPortLayout,
  deviceSupportsPorts,
  deviceSupportsWifiAntennas,
  suggestSizeForPorts,
} from './devicePorts.js';
import {
  defaultDeviceInterfaces,
  interfacesWiredCount,
} from './deviceInterfaces.js';

const CRITICAL: DeviceType[] = [
  'switch',
  'router',
  'firewall',
  'server',
  'controller',
  'modem',
  'lte_antenna',
  'ups',
  'power_station',
];
const PRESENCE: DeviceType[] = [
  'laptop',
  'tablet',
  'display',
  'wifi_phone',
  'desktop',
  'printer',
  'camera',
  'iot',
];
const PASSIVE: DeviceType[] = ['switch', 'zone', 'ups', 'power_station'];

export function createNode(
  type: DeviceType,
  position: { x: number; y: number },
  id: string,
): DiagramNode {
  const monitorKey =
    type === 'zone'
      ? ''
      : `${type.toUpperCase().replaceAll('_', '-')}-${id.slice(0, 4).toUpperCase()}`;

  const interfaces =
    deviceSupportsPorts(type) || deviceSupportsWifiAntennas(type)
      ? defaultDeviceInterfaces(type)
      : undefined;
  const portCount = interfaces ? interfacesWiredCount(interfaces) : 0;
  const wifiAntennaCount = interfaces?.hasWifi ? interfaces.wifiCount ?? 0 : 0;
  const portLayout = deviceSupportsPorts(type) ? defaultPortLayout(type) : undefined;
  const size =
    type === 'zone'
      ? { width: 320, height: 220 }
      : suggestSizeForPorts(portCount, undefined, portLayout ?? 'both', wifiAntennaCount);

  const node: DiagramNode = {
    id,
    monitorKey,
    type,
    label: type === 'zone' ? 'Зона' : monitorKey,
    description: '',
    zoneId: null,
    position: snapPosition(position),
    size,
    criticality: CRITICAL.includes(type) ? 'critical' : 'normal',
    monitorMode: PASSIVE.includes(type)
      ? 'passive'
      : PRESENCE.includes(type)
        ? 'dhcp_presence'
        : type === 'router' || type === 'firewall' || type === 'modem'
          ? 'router_interface'
          : 'icmp',
    expectedPresence: PRESENCE.includes(type) ? 'optional' : 'always',
    maintenanceUntil: null,
    notes: '',
    locked: false,
  };

  if (deviceSupportsPorts(type)) {
    node.portCount = portCount;
    node.portLayout = portLayout;
  }
  if (interfaces) {
    node.interfaces = interfaces;
  }
  if (deviceSupportsWifiAntennas(type) && wifiAntennaCount > 0) {
    node.wifiAntennaCount = wifiAntennaCount;
  }

  return node;
}

export function createEmptyDiagram(
  siteId: string,
  name: string,
  lifecycle: NetworkLifecycle = 'draft',
): DiagramDocument {
  return {
    schemaVersion: 1,
    id: `diagram-${siteId}`,
    siteId,
    name,
    updatedAt: new Date().toISOString(),
    nodes: [],
    edges: [],
    lifecycle,
  };
}
